import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Chat from "@/pages/Chat";
import Insights from "@/pages/Insights";
import Patterns from "@/pages/Patterns";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import AppLayout from "@/components/AppLayout";

import "@/App.css";

function Protected({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-brand-cream">
                <div className="text-brand-darkPurple/60 font-serif italic text-xl">gathering the light…</div>
            </div>
        );
    }
    if (!user) {
        const next = encodeURIComponent(location.pathname + location.search);
        // Preserve destination + surface a soft "session expired" hint if the user
        // was on an authed path AND had a session in this tab (distinguishes
        // "session lost mid-flight" from "first-ever anonymous visit to a protected URL").
        const isAuthedPath = ["/dashboard", "/chat", "/insights", "/patterns", "/profile", "/settings", "/onboarding"]
            .some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
        let hadSession = false;
        try { hadSession = sessionStorage.getItem("tc_had_session") === "1"; } catch { /* ignore */ }
        const suffix = isAuthedPath && hadSession ? `?next=${next}&reason=expired` : `?next=${next}`;
        return <Navigate to={`/login${suffix}`} replace />;
    }
    if (!user.onboarded && location.pathname !== "/onboarding") {
        return <Navigate to="/onboarding" replace />;
    }
    return children;
}

function AppRouter() {
    const location = useLocation();
    // Handle Google OAuth callback synchronously during render
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/onboarding" element={
                <Protected><Onboarding /></Protected>
            } />
            <Route element={<Protected><AppLayout /></Protected>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/chat/:conversationId" element={<Chat />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/patterns" element={<Patterns />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <AppRouter />
                <Toaster richColors position="top-center" />
            </BrowserRouter>
        </AuthProvider>
    );
}
